import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from 'react-hook-form';
import { DentalSignUpValidate } from '@/utils/form.utils';
import * as yup from "yup";
import Container from '@/components/common/elements/Container';
import Text from '@/components/common/elements/Text';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useIsMobile, useValidateInput } from '@/common/hooks';
import { useSetRecoilState } from 'recoil';
import { toastState } from '@/state/toastState';
import { SignupMeta, ClinicsSignUpRequest } from '@/api/repository/types/types';
import SelectBox, { Option } from '@/components/common/elements/SelectBox';
import AddressSearchFiled from '@/components/common/organism/AddressSearchFiled';
import { KakaoAddressDocument } from '@/common/types';
import { filterNumericInput } from '@/utils/filterNumberInput';
import { convertToDate } from '@/utils/convertToDate';
import { addressCodeFields } from '@/utils/regionLabel';
import RegionSelector from '@/components/common/organism/RegionSelector';
import { api } from '@/api/repository/axios';
import ImageCropModal from '@/components/common/organism/ImageCropModal';
import { useResultModal } from '@/hooks/useResultModal';
import HomepagePhotoPicker from '@/components/domain/clinic/HomepagePhotoPicker';
import type { HospitalStepResult } from './ClinicHospitalVerifyStep';
import type { HomepagePreview } from '@/api/repository/types/types';
import { businessNumberDigits, formatBusinessNumber } from '@/utils/clinicFields';

import { designSystem } from '@/styles/theme';

// Styled Components - 속닥속닥 스타일
const FormContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 8px 0;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
`;

const Label = styled.label`
    font-size: 0.95rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;

    span {
        color: #dc2626;
        margin-left: 2px;
    }
`;

const HelpText = styled.p`
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0 0 8px 0;
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
    width: 100%;
    padding: 14px 16px;
    border: none;
    border-bottom: 1px solid ${props => props.hasError ? '#dc2626' : '#e5e7eb'};
    border-radius: 0;
    font-size: 1rem;
    color: #1a1a1a;
    background-color: #fff;
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;

    &:focus {
        border-bottom-color: ${props => props.hasError ? '#dc2626' : designSystem.colors.primary};
    }

    &::placeholder {
        color: #9ca3af;
    }

    &:disabled {
        background-color: #f9fafb;
        color: #6b7280;
    }

    /* 크롬/네이버 패스워드 매니저 autofill 시 노랑/파랑 배경 제거 */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active,
    &:-internal-autofill-selected {
        -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
        box-shadow: 0 0 0 1000px #fff inset !important;
        -webkit-text-fill-color: #1a1a1a !important;
        background-color: #fff !important;
        background-image: none !important;
        transition: background-color 5000s ease-in-out 0s !important;
    }

    @media (max-width: 768px) {
        padding: 12px 14px;
        font-size: 0.95rem;
    }
`;

const StyledTextArea = styled.textarea<{ hasError?: boolean }>`
    width: 100%;
    padding: 14px 16px;
    border: 1px solid ${props => props.hasError ? '#dc2626' : '#e5e7eb'};
    border-radius: 8px;
    font-size: 1rem;
    color: #1a1a1a;
    background-color: #fff;
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
    resize: vertical;
    min-height: 100px;
    font-family: inherit;

    &:focus {
        border-color: ${props => props.hasError ? '#dc2626' : designSystem.colors.primary};
    }

    &::placeholder {
        color: #9ca3af;
    }

    @media (max-width: 768px) {
        padding: 12px 14px;
        font-size: 0.95rem;
    }
`;

const ErrorMessage = styled.p`
    font-size: 0.85rem;
    color: #dc2626;
    margin: 4px 0 0 0;
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const ValidMessage = styled.span<{ color?: string }>`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.85rem;
    font-weight: 600;
    color: ${props => props.color};
    white-space: nowrap;
    pointer-events: none;
`;

const SectionDivider = styled.div`
    height: 1px;
    background-color: #f0f0f0;
    margin: 8px 0;
`;

const SectionTitle = styled.h2`
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 16px 0 0 0;
    padding-bottom: 8px;
    border-bottom: 2px solid ${designSystem.colors.primary};
    display: inline-block;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    padding-top: 24px;
    margin-top: 16px;
    border-top: 1px solid #f0f0f0;
`;

const CancelButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 24px;
    background-color: #f3f4f6;
    color: #374151;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e5e7eb;
    }

    @media (max-width: 768px) {
        flex: 1;
        justify-content: center;
        padding: 10px 20px;
        font-size: 14px;
    }
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
    padding: 12px 24px;
    background-color: ${props => props.disabled ? '#e2e8f0' : designSystem.colors.primary};
    color: ${props => props.disabled ? '#94a3b8' : '#ffffff'};
    border: none;
    border-radius: 8px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-size: 15px;
    font-weight: 600;
    transition: all 0.2s ease;
    box-shadow: ${props => props.disabled ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.2)'};

    &:hover {
        background-color: ${props => props.disabled ? '#e2e8f0' : designSystem.colors.primaryDark};
    }

    @media (max-width: 768px) {
        flex: 1;
        padding: 10px 20px;
        font-size: 14px;
    }
`;

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;
`;

/** 보조 버튼(흰 바탕·청록 테두리) — 홈페이지에서 가져오기·제안 채우기 */
const SubButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border: 1px solid ${designSystem.colors.primary};
    border-radius: 8px;
    background: #fff;
    color: #2F6B66;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/** 홈페이지에서 찾은 값 제안 — 회원이 [채우기]를 눌러야 들어간다 */
const SuggestBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #F1F8F6;
    font-size: 13px;
    color: #4F5B58;
`;

const SuggestRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;

    span {
        min-width: 0;
        word-break: break-all;
    }

    b {
        color: #1F2A28;
    }

    button {
        min-height: 36px;
        padding: 0 12px;
        border: 1px solid #D3DBD8;
        border-radius: 8px;
        background: #fff;
        color: #2F6B66;
        font-family: inherit;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }
`;

import { TermsId, TERMS_VERSION } from '@/pages/Signup/termsContent';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

/** 가입 완료 화면에 넘기는 값 — 인증 완료면 채용 공고 예시 카드를 이 값으로 그린다 */
export interface ClinicSignupDone {
    clinicVerified: boolean;
    clinicName: string;
    address: string;
    thumbnail: string;
}

interface UserInputProps {
    /** 이전(병원 인증 단계로) */
    onBack: () => void;
    onDone: (done: ClinicSignupDone) => void;
    agreements?: Record<TermsId, boolean>;
    /** 병원 인증 단계 결과 — 이메일·사업자등록번호·대표자·개업연월일과 확인 결과, 미리 채울 값 */
    hospitalStep: HospitalStepResult;
}

type DentalSignUpFormData = yup.InferType<typeof DentalSignUpValidate>;

const SummaryBox = styled.section`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    border-radius: 12px;
    border: 1px solid #D3DBD8;
    background: #F5F7F6;
    font-size: 14px;
    color: #1F2A28;

    p {
        margin: 0;
        line-height: 1.5;
        word-break: keep-all;
    }

    span {
        color: #4F5B58;
    }
`;

const SubmitNote = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: #4F5B58;
    text-align: center;
    word-break: keep-all;
`;

/** 'YYYYMMDD' → 그날 0시 ISO(개원일 칸 값) */
const isoOfDigits = (digits: string): string => {
    const date = convertToDate(digits);
    return date ? date.toISOString() : '';
};

const DentalSignUpForm = ({ onBack, onDone, agreements, hospitalStep }: UserInputProps) => {
    const hospital = hospitalStep.hospital ?? null;
    const isMobile = useIsMobile();
    const setToast = useSetRecoilState(toastState);
    const { showError, showErrorMessage, resultModal } = useResultModal();
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 썸네일 16:9 크롭 모달용 원본 파일
    const [cropSource, setCropSource] = useState<File | null>(null);
    const [thumbUploading, setThumbUploading] = useState(false);
    const thumbInputRef = useRef<HTMLInputElement>(null);
    const [totalWorkers, setTotalWorkers] = useState("");
    // 개원일 — 확인된 병원의 심평원 개설일, 없으면 병원 인증의 개업연월일로 미리 채운다(다르면 고친다)
    const sinceDigits: string = /^\d{8}$/.test(hospital?.establishedOn ?? '') ? (hospital?.establishedOn as string) : hospitalStep.openingDate;
    const [since, setSince] = useState(sinceDigits);
    // 주소 — 병원 인증 단계에서 고른 값 그대로(주소 검색 부품이 기본값으로 다시 찾아 지역·좌표를 채운다, 고칠 수 있다 — 고치면 가입 때 서버가 다시 대조)
    const [roadAddressName, setRoadAddressName] = useState<string>(hospitalStep.address ?? hospitalStep.hospital?.address ?? "");
    /** 고른 주소의 카카오 b_code 원문 — 지역 판정은 서버가 한다 */
    const [addressCode, setAddressCode] = useState<string | undefined>(undefined);
    const [signupMeta, setSignupMeta] = useState<SignupMeta | null>(null);
    const [hospitalType, setHospitalType] = useState<Option>({ description: "", type: "" });
    const [isHospitalTypeOpen, setIsHospitalTypeOpen] = useState(false);
    const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    // 담당자 이메일(managerEmail)이 로그인 이메일 → 인증 완료 여부
    // 이메일은 병원 인증 단계에서 인증했다
    const emailVerified: boolean = true;
    // 홈페이지에서 찾은 값(제안)·대표 사진 후보
    const [homepagePreview, setHomepagePreview] = useState<HomepagePreview | null>(null);
    const [homepageLoading, setHomepageLoading] = useState<boolean>(false);
    const [homepageMessage, setHomepageMessage] = useState<string>('');
    const [pickingPhoto, setPickingPhoto] = useState<string | null>(null);
    const [pickedPhoto, setPickedPhoto] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        setValue,
        watch,
    } = useForm({
        resolver: yupResolver(DentalSignUpValidate),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            password: '',
            hospitalName: hospital?.name ?? hospitalStep.clinicName,
            since: isoOfDigits(sinceDigits),
            hospitalTel: hospital?.phone ?? '',
            ownerName: hospitalStep.ownerName,
            licenseNumber: hospitalStep.businessNumber,
            email: hospitalStep.email,
            recruitmentManagerName: '',
            recruitmentManagerEmail: '',
            recruitmentManagerTel: '',
            address: '',
            detailAddress: '',
            subwayStation: [],
            hospitalType: '',
            hospitalDescription: '',
            employeeDescription: '',
            url: hospital?.homepage ?? hospitalStep.homepageUrl ?? '',
            thumbnailImage: '',
        },
    });

    const watchValue = watch();

    // react-hook-form 필드명 → 사용자 표시 라벨 (handleInvalidSubmit 알림에 사용)
    const FIELD_LABEL_MAP: Record<string, string> = {
        password: '비밀번호',
        hospitalName: '치과명',
        hospitalType: '치과 구분',
        since: '개원일',
        hospitalTel: '치과 전화번호',
        ownerName: '대표자명',
        recruitmentManagerName: '채용담당자',
        recruitmentManagerEmail: '채용담당자 이메일',
        recruitmentManagerTel: '채용담당자 전화번호',
        address: '주소',
        detailAddress: '상세주소',
        licenseNumber: '사업자등록번호',
        url: '치과 홈페이지',
    };

    const scrollToError = (fieldName: keyof DentalSignUpFormData | string) => {
        const target = document.querySelector(
            `[name="${fieldName}"], [data-field="${fieldName}"]`
        ) as HTMLElement | null;
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => target.focus?.(), 300);
        }
        // fallback 으로 userIdRef 로 가지 않음 — 사용자가 왜 거기로 갔는지 모를 수 있음.
        // 누락 필드 알림은 handleInvalidSubmit / onSubmit 의 alert 로 처리.
    };

    const FIELD_NAME_MAP: Record<string, string> = {
        "비밀번호": "password",
        "치과명": "hospitalName",
        "개원일": "since",
        "치과 전화번호": "hospitalTel",
        "대표자명": "ownerName",
        "채용담당자": "recruitmentManagerName",
        "채용담당자 이메일": "recruitmentManagerEmail",
        "채용담당자 전화번호": "recruitmentManagerTel",
        "주소": "address",
        "사업자등록번호": "licenseNumber",
        "치과 홈페이지": "url",
        "사업자등록증 사진": "businessCert",
        "썸네일 이미지": "thumbnailImage",
    };

    const handleInvalidSubmit = (errors: any) => {
        const errorKeys = Object.keys(errors);
        if (errorKeys.length === 0) return;
        const firstErrorField = errorKeys[0] as keyof DentalSignUpFormData;
        scrollToError(firstErrorField);
    };

    const validateFields = () => {
        const fieldsToCheck = [
            { value: watchValue.password, name: "비밀번호" },
            { value: watchValue.hospitalName, name: "치과명" },
            { value: since, name: "개원일" },
            { value: watchValue.hospitalTel, name: "치과 전화번호" },
            { value: watchValue.ownerName, name: "대표자명" },
            { value: watchValue.recruitmentManagerName, name: "채용담당자" },
            { value: watchValue.recruitmentManagerEmail, name: "채용담당자 이메일" },
            { value: watchValue.recruitmentManagerTel, name: "채용담당자 전화번호" },
            { value: roadAddressName, name: "주소" },
            { value: watchValue.licenseNumber, name: "사업자등록번호" },
            { value: watchValue.thumbnailImage, name: "썸네일 이미지" },
        ];

        return fieldsToCheck
            .filter(field => !field.value || (Array.isArray(field.value) && field.value.length === 0))
            .map(field => field.name);
    };

    const onSubmit = (data: DentalSignUpFormData) => {
        const updatedMissingFields = validateFields();

        if (updatedMissingFields.length) {
            setMissingFields(updatedMissingFields);
            const firstField = FIELD_NAME_MAP[updatedMissingFields[0]];
            if (firstField) {
                scrollToError(firstField as keyof DentalSignUpFormData);
            }
            return;
        }

        // 비밀번호 확인 일치 검증 — 빈 값/불일치 모두 스크롤 + 필드 빨간 글씨(조용한 차단 방지)
        if (!passwordConfirm || passwordConfirm !== watchValue.password) {
            setMissingFields(['비밀번호 확인']);
            scrollToError('password');
            return;
        }

        // 로그인 이메일 인증 미완료 시 해당 필드로 스크롤 + 필드 빨간 글씨
        if (!emailVerified) {
            setMissingFields(['이메일']);
            scrollToError('email');
            return;
        }

        const payload = {
            email: watchValue.email,
            password: watchValue.password,
            clinic: {
                name: watchValue.hospitalName,
                since: watchValue.since,
                phoneNumber: watchValue.hospitalTel,
                ownerName: watchValue.ownerName,
                managerName: watchValue.recruitmentManagerName,
                managerEmail: watchValue.recruitmentManagerEmail,
                managerPhoneNumber: watchValue.recruitmentManagerTel,
                description: watchValue.hospitalDescription,
                totalWorkers: Number(totalWorkers),
                totalWorkersDescription: watchValue.employeeDescription,
                address: roadAddressName,
                addressDetail: watchValue.detailAddress,
                districtId: watchValue.address,
                addressCode: addressCode,
                stationIds: watchValue.subwayStation,
                url: watchValue.url,
                // 병원 인증 단계 결과 — 서버가 다시 검사한다
                hospitalCheckToken: hospital?.token ?? undefined,
                homepageCheckToken: hospitalStep.homepageToken ?? undefined,
                businessCertToken: hospitalStep.businessCertToken ?? undefined,
                // 병원 인증 단계의 사업자등록증 개업연월일 — 국세청 진위확인용(개원일 칸은 심평원 개설일로 채워질 수 있다)
                ntsOpeningDate: /^\d{8}$/.test(hospitalStep.openingDate ?? '') ? hospitalStep.openingDate : undefined,
                // 백엔드 DTO(ApiClinicRequest)는 thumbnail 이 아니라 images/mobileImages 를 받는다.
                // 썸네일 1장을 PC/모바일 이미지 배열로 전달해야 clinic_images 에 저장됨.
                images: watchValue.thumbnailImage ? [watchValue.thumbnailImage] : [],
                mobileImages: watchValue.thumbnailImage ? [watchValue.thumbnailImage] : [],
                latitude: position.latitude,
                longitude: position.longitude,
                clinicType: watchValue.hospitalType,
                // 사업자등록번호는 새 칸(business_number) — 면허번호 칸과 다르다
                businessNumber: businessNumberDigits(watchValue.licenseNumber),
            },
            authority: 'ROLE_CLINIC',
            agreements: agreements,
            termsVersion: TERMS_VERSION,
        };

        setIsSubmitting(true);
        // 폼 검증을 통과한 값이므로 필수 필드는 실제로는 채워져 있음 (RHF 값 타입은 string | undefined)
        api.clinicsSignUp(payload as ClinicsSignUpRequest)
            .then((res) => {
                if (res.message === "성공") {
                    onDone({
                        clinicVerified: !!res.result?.clinicVerified,
                        clinicName: watchValue.hospitalName ?? '',
                        address: roadAddressName,
                        thumbnail: watchValue.thumbnailImage || '/default-clinic.png',
                    });
                } else {
                    showErrorMessage(res.message || '회원가입에 실패했습니다.');
                }
            })
            .catch((error) => {
                console.error('clinicsSignUp error:', error);
                // 서버 원문(트랜잭션/스택 등) 노출 방지 — 정책에 따라 안전한 문구로. 미정의 에러는 일반 문구.
                showError(error, '회원가입 실패');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleUserTypeChange = (selected: Option) => {
        setHospitalType(selected);
        setValue("hospitalType", selected.type);
    };

    const handleSinceChange = (value: string) => {
        const filterNumber = filterNumericInput(value);
        const convertToDateValue = convertToDate(filterNumber);
        if (convertToDateValue) {
            const isoString = convertToDateValue.toISOString();
            setValue("since", isoString);
        }
        setSince(filterNumber);
    };

    const handleAddress = (address: KakaoAddressDocument) => {
        const fields = addressCodeFields(address.address?.b_code);
        setValue("address", fields.districtId ?? '');
        setAddressCode(fields.addressCode);
        setRoadAddressName(address.address_name);
        const pos = {
            latitude: address.y,
            longitude: address.x
        };
        setPosition(pos);
    };

    // missingFields 자동 클린업: 사용자가 누락 필드를 채우면 빨간 underline 즉시 제거
    useEffect(() => {
        if (missingFields.length === 0) return;
        const stillMissing: string[] = missingFields.filter((f) => {
            if (f === '비밀번호') return !watchValue.password;
            if (f === '비밀번호 확인') return !passwordConfirm || passwordConfirm !== watchValue.password;
            if (f === '치과명') return !watchValue.hospitalName;
            if (f === '개원일') return !since;
            if (f === '치과 전화번호') return !watchValue.hospitalTel;
            if (f === '대표자명') return !watchValue.ownerName;
            if (f === '채용담당자') return !watchValue.recruitmentManagerName;
            if (f === '채용담당자 이메일') return !watchValue.recruitmentManagerEmail;
            if (f === '채용담당자 전화번호') return !watchValue.recruitmentManagerTel;
            if (f === '주소') return !roadAddressName;
            if (f === '사업자등록번호') return !watchValue.licenseNumber;
            if (f === '썸네일 이미지') return !watchValue.thumbnailImage;
            return true;
        });
        if (stillMissing.length !== missingFields.length) {
            setMissingFields(stillMissing);
        }
    }, [
        watchValue.password, passwordConfirm,
        watchValue.hospitalName, since, watchValue.hospitalTel,
        watchValue.ownerName, watchValue.recruitmentManagerName,
        watchValue.recruitmentManagerEmail, watchValue.recruitmentManagerTel,
        roadAddressName, watchValue.thumbnailImage, watchValue.licenseNumber, watchValue.url,
    ]);

    /** 홈페이지에서 대표 사진 후보·대표자·주소 제안 찾기 — 가입 중(이메일 인증 뒤)에만 서버가 받는다 */
    const loadHomepage = async () => {
        if (!emailVerified) {
            setHomepageMessage('이메일 인증을 먼저 마쳐 주세요.');
            scrollToError('email');
            return;
        }
        if (!watchValue.url || errors.url) {
            setHomepageMessage('치과 홈페이지 주소를 먼저 바르게 입력해 주세요.');
            return;
        }
        setHomepageLoading(true);
        setHomepageMessage('');
        try {
            const res = await api.signup.homepagePreview(watchValue.email ?? '', watchValue.url);
            const preview: HomepagePreview | undefined = res.result;
            if (!preview) {
                setHomepageMessage(res.message || '홈페이지를 읽지 못했어요.');
                return;
            }
            setHomepagePreview(preview);
            if (preview.failure) {
                setHomepageMessage(preview.failure);
            } else if (preview.thumbnailCandidates.length === 0) {
                setHomepageMessage('홈페이지에서 쓸 만한 큰 사진을 찾지 못했어요. 아래에서 직접 올려 주세요.');
            }
        } catch (error: any) {
            setHomepageMessage(error?.response?.data?.message || '홈페이지를 읽지 못했어요. 잠시 뒤 다시 시도해 주세요.');
        } finally {
            setHomepageLoading(false);
        }
    };

    /** 고른 사진 — 서버가 내려받아 16:9·압축해 저장한 주소로 썸네일 칸을 채운다 */
    const pickHomepagePhoto = async (imageUrl: string) => {
        setPickingPhoto(imageUrl);
        try {
            const res = await api.signup.homepageThumbnail(watchValue.email ?? '', imageUrl);
            if (res.result?.url) {
                setValue('thumbnailImage', res.result.url, { shouldValidate: true });
                setPickedPhoto(imageUrl);
            } else {
                showErrorMessage(res.message || '사진을 가져오지 못했어요.');
            }
        } catch (error: any) {
            showErrorMessage(error?.response?.data?.message || '사진을 가져오지 못했어요.');
        } finally {
            setPickingPhoto(null);
        }
    };

    useEffect(() => {
        const fetchClinicsSignupMeta = async () => {
            try {
                const response = await api.signup.clinicsMeta();
                setSignupMeta(response.result);
            } catch (error) {
                console.error("Failed to fetch clinics signup meta:", error);
            }
        };

        fetchClinicsSignupMeta();
    }, []);

    return (
        <FormContent>
            {resultModal}
            <SectionTitle>기본정보</SectionTitle>

            <SummaryBox aria-label="병원 인증 정보">
                <p><span>로그인 이메일</span> {hospitalStep.email}</p>
                <p><span>사업자등록번호</span> {formatBusinessNumber(hospitalStep.businessNumber)} · <span>대표자</span> {hospitalStep.ownerName}</p>
                {hospital && <p><span>확인된 병원</span> {hospital.name}</p>}
                <SubButton type="button" onClick={onBack} style={{ alignSelf: 'flex-start', marginTop: 4 }}>병원 인증 정보 바꾸기</SubButton>
            </SummaryBox>

            <FormGroup>
                <Label>비밀번호<span>*</span></Label>
                <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            type="password"
                            placeholder="비밀번호를 입력해주세요"
                            hasError={missingFields.includes('비밀번호') || !!errors.password}
                        />
                    )}
                />
                {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>비밀번호 확인<span>*</span></Label>
                <InputWrapper>
                    <StyledInput
                        type="password"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        placeholder="비밀번호를 다시 한 번 입력해주세요"
                        hasError={missingFields.includes('비밀번호 확인') || (passwordConfirm.length > 0 && passwordConfirm !== watchValue.password)}
                    />
                    {passwordConfirm.length > 0 && (
                        <ValidMessage color={passwordConfirm === watchValue.password ? '#10b981' : '#dc2626'}>
                            {passwordConfirm === watchValue.password ? '일치' : '불일치'}
                        </ValidMessage>
                    )}
                </InputWrapper>
                {passwordConfirm.length === 0 && missingFields.includes('비밀번호 확인') && (
                    <ErrorMessage>비밀번호 확인을 입력해주세요.</ErrorMessage>
                )}
            </FormGroup>

            <FormGroup>
                <Label>치과명<span>*</span></Label>
                <Controller
                    name="hospitalName"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="치과명을 입력해주세요"
                            hasError={missingFields.includes('치과명') || !!errors.hospitalName}
                        />
                    )}
                />
                {errors.hospitalName && <ErrorMessage>{errors.hospitalName.message}</ErrorMessage>}
            </FormGroup>

            <SectionDivider />
            <SectionTitle>치과 정보</SectionTitle>

            <FormGroup>
                <Label>개원일<span>*</span></Label>
                <StyledInput
                    data-field="since"
                    name="since"
                    value={since}
                    onChange={(e) => handleSinceChange(e.target.value)}
                    placeholder="YYYYMMDD"
                    maxLength={8}
                    hasError={missingFields.includes('개원일') || !!errors.since}
                />
                {errors.since && <ErrorMessage>{errors.since.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>치과 전화번호<span>*</span></Label>
                <Controller
                    name="hospitalTel"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="치과 전화번호를 입력해주세요"
                            hasError={missingFields.includes('치과 전화번호') || !!errors.hospitalTel}
                        />
                    )}
                />
                {errors.hospitalTel && <ErrorMessage>{errors.hospitalTel.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>총 직원 수</Label>
                <StyledInput
                    value={totalWorkers}
                    onChange={(e) => setTotalWorkers(e.target.value)}
                    placeholder="총 직원 수를 입력해주세요"
                />
            </FormGroup>

            <FormGroup>
                <Label>치과 유형<span>*</span></Label>
                <SelectWrapper>
                    <Controller
                        name="hospitalType"
                        control={control}
                        render={({ field }) => (
                            <StyledInput
                                {...field}
                                value={hospitalType.description ?? ""}
                                placeholder="치과 유형을 선택해주세요"
                                readOnly
                                style={{ cursor: 'pointer' }}
                                onFocus={() => setIsHospitalTypeOpen(!isHospitalTypeOpen)}
                            />
                        )}
                    />
                    <SelectBox
                        onOpenContrall={(open) => setIsHospitalTypeOpen(open)}
                        options={signupMeta?.clinicTypes ?? null}
                        onChange={(selected) => handleUserTypeChange(selected)}
                        isOpen={isHospitalTypeOpen}
                    />
                </SelectWrapper>
            </FormGroup>

            <FormGroup>
                <Label>치과 설명</Label>
                <Controller
                    name="hospitalDescription"
                    control={control}
                    render={({ field }) => (
                        <StyledTextArea
                            {...field}
                            placeholder="치과 소개 및 설명을 입력해주세요"
                        />
                    )}
                />
            </FormGroup>

            <SectionDivider />
            <SectionTitle>채용담당자 정보</SectionTitle>

            <FormGroup>
                <Label>담당자명<span>*</span></Label>
                <Controller
                    name="recruitmentManagerName"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="채용담당자명을 입력해주세요"
                            hasError={missingFields.includes('채용담당자') || !!errors.recruitmentManagerName}
                        />
                    )}
                />
                {errors.recruitmentManagerName && <ErrorMessage>{errors.recruitmentManagerName.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>담당자 이메일<span>*</span></Label>
                <Controller
                    name="recruitmentManagerEmail"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            type="email"
                            placeholder="담당자 이메일을 입력해주세요"
                            hasError={missingFields.includes('채용담당자 이메일') || !!errors.recruitmentManagerEmail}
                        />
                    )}
                />
                {errors.recruitmentManagerEmail && <ErrorMessage>{errors.recruitmentManagerEmail.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>담당자 전화<span>*</span></Label>
                <Controller
                    name="recruitmentManagerTel"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="채용담당자 전화번호를 입력해주세요"
                            hasError={missingFields.includes('채용담당자 전화번호') || !!errors.recruitmentManagerTel}
                        />
                    )}
                />
                {errors.recruitmentManagerTel && <ErrorMessage>{errors.recruitmentManagerTel.message}</ErrorMessage>}
            </FormGroup>

            <SectionDivider />
            <SectionTitle>주소 정보</SectionTitle>

            <FormGroup data-field="address">
                <Label>주소<span>*</span></Label>
                {!hospital && hospitalStep.homepageAddresses?.[0] && !roadAddressName && (
                    <HelpText>홈페이지 주소: {hospitalStep.homepageAddresses[0]} — 이 주소로 검색해 골라 주세요.</HelpText>
                )}
                <AddressSearchFiled
                    defaultValue={roadAddressName}
                    onChange={handleAddress}
                    height={48}
                    width={'100%'}
                    border={'none'}
                    borderBottom={'1px solid #e5e7eb'}
                    margin='0'
                />
                {errors.address && <ErrorMessage>{errors.address.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>상세주소<span>*</span></Label>
                <Controller
                    name="detailAddress"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="상세주소를 입력해주세요"
                            hasError={!!errors.detailAddress}
                        />
                    )}
                />
                {errors.detailAddress && <ErrorMessage>{errors.detailAddress.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>인근 지하철역 <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 'normal' }}>(선택)</span></Label>
                <RegionSelector
                    onSelectedSubwayItem={(item) => setValue("subwayStation", item)}
                />
            </FormGroup>

            <SectionDivider />
            <SectionTitle>추가 정보</SectionTitle>

            <FormGroup>
                <Label>치과 홈페이지 <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 'normal' }}>(선택)</span></Label>
                <HelpText>홈페이지가 있으면 적어 주세요. 대표 사진을 가져올 수 있어요.</HelpText>
                <Controller
                    name="url"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            inputMode="url"
                            placeholder="예: www.치과.com"
                            hasError={!!errors.url}
                        />
                    )}
                />
                {errors.url && <ErrorMessage>{errors.url.message}</ErrorMessage>}
                <SubButton type="button" onClick={loadHomepage} disabled={homepageLoading}>
                    {homepageLoading ? <ButtonSpinner /> : '홈페이지에서 대표 사진 가져오기'}
                </SubButton>
                {homepageMessage && <HelpText role="status" style={{ margin: 0 }}>{homepageMessage}</HelpText>}
                {homepagePreview && !homepagePreview.failure && (
                    <>
                        {(() => {
                            const owner: string | undefined = homepagePreview.ownerNames[0];
                            const number: string | undefined = homepagePreview.businessNumbers[0];
                            const phone: string | undefined = homepagePreview.phones[0];
                            const address: string | undefined = homepagePreview.addresses[0];
                            // 대표자·사업자등록번호는 병원 인증 단계 값이라 여기서 바꾸지 않는다
                            const showOwner: boolean = false && !!owner;
                            const showNumber: boolean = false && !!number;
                            const showPhone: boolean = !!phone && phone.replace(/[^0-9]/g, '') !== (watchValue.hospitalTel ?? '').replace(/[^0-9]/g, '');
                            if (!showOwner && !showNumber && !showPhone && !address) return null;
                            return (
                                <SuggestBox aria-label="홈페이지에서 찾은 값">
                                    {showOwner && (
                                        <SuggestRow>
                                            <span>홈페이지 대표자 <b>{owner}</b></span>
                                            <button type="button" onClick={() => setValue('ownerName', owner as string, { shouldValidate: true })}>대표자명에 채우기</button>
                                        </SuggestRow>
                                    )}
                                    {showNumber && (
                                        <SuggestRow>
                                            <span>홈페이지 사업자등록번호 <b>{number}</b></span>
                                            <button type="button" onClick={() => setValue('licenseNumber', businessNumberDigits(number), { shouldValidate: true })}>채우기</button>
                                        </SuggestRow>
                                    )}
                                    {showPhone && (
                                        <SuggestRow>
                                            <span>홈페이지 전화 <b>{phone}</b></span>
                                            <button type="button" onClick={() => setValue('hospitalTel', phone as string, { shouldValidate: true })}>치과 전화번호에 채우기</button>
                                        </SuggestRow>
                                    )}
                                    {address && (
                                        <SuggestRow>
                                            <span>홈페이지 주소 <b>{address}</b> — 주소 칸에서 이 주소로 검색해 주세요.</span>
                                        </SuggestRow>
                                    )}
                                </SuggestBox>
                            );
                        })()}
                        {homepagePreview.thumbnailCandidates.length > 0 && (
                            <>
                                <HelpText style={{ margin: 0 }}>대표 사진으로 쓸 사진을 골라 주세요(16:9로 잘라 저장해요).</HelpText>
                                <HomepagePhotoPicker
                                    candidates={homepagePreview.thumbnailCandidates}
                                    previews={homepagePreview.thumbnailPreviews}
                                    pickingUrl={pickingPhoto}
                                    pickedUrl={pickedPhoto}
                                    onPick={pickHomepagePhoto}
                                />
                            </>
                        )}
                    </>
                )}
            </FormGroup>

            <FormGroup>
                <Label>직원 설명</Label>
                <Controller
                    name="employeeDescription"
                    control={control}
                    render={({ field }) => (
                        <StyledTextArea
                            {...field}
                            placeholder="직원 구성 및 업무 설명을 입력해주세요"
                        />
                    )}
                />
            </FormGroup>

            <FormGroup data-field="thumbnailImage">
                <Label>썸네일 이미지<span>*</span></Label>
                <Controller
                    name="thumbnailImage"
                    control={control}
                    render={({ field }) => {
                        const isDefaultImg = field.value === '/default-clinic.png';
                        const hasImage = !!field.value;
                        const openPicker = () => { if (!thumbUploading) thumbInputRef.current?.click(); };
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '440px' }}>
                                {/* 선택/변경 공용 숨김 파일 입력 → 16:9 크롭 모달 */}
                                <input
                                    ref={thumbInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (!file) return;
                                        if (file.size > 10 * 1024 * 1024) {
                                            showErrorMessage('파일 크기가 10MB를 초과합니다.');
                                            return;
                                        }
                                        setCropSource(file);
                                    }}
                                />
                                <ImageCropModal
                                    file={cropSource}
                                    onCancel={() => setCropSource(null)}
                                    onCropped={async (cropped) => {
                                        setCropSource(null);
                                        setThumbUploading(true);
                                        try {
                                            const res = await api.upload.image(cropped);
                                            if (res.code === 0 && res.result?.url) {
                                                field.onChange(res.result.url);
                                            } else {
                                                showErrorMessage('이미지 업로드에 실패했습니다.');
                                            }
                                        } catch (error) {
                                            console.error('이미지 업로드 실패:', error);
                                            showErrorMessage('이미지 업로드에 실패했습니다.');
                                        } finally {
                                            setThumbUploading(false);
                                        }
                                    }}
                                />

                                {!hasImage ? (
                                    // 빈 상태: 점선 업로드 박스 + 기본 이미지
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <button
                                            type="button"
                                            onClick={openPicker}
                                            disabled={thumbUploading}
                                            style={{ width: '240px', aspectRatio: '16 / 9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '1.5px dashed #b8d4cd', borderRadius: '10px', background: '#f7fbfa', color: '#3f8576', cursor: thumbUploading ? 'not-allowed' : 'pointer' }}
                                        >
                                            <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 30 }} />
                                            <span style={{ fontSize: '14px', fontWeight: 700 }}>{thumbUploading ? <ButtonSpinner /> : '이미지 선택'}</span>
                                            <span style={{ fontSize: '11px', color: '#8B8D94' }}>JPG · PNG · WEBP · 16:9</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => field.onChange('/default-clinic.png')}
                                            style={{ padding: '9px 16px', fontSize: '13px', fontWeight: 600, border: '1px solid #5b9a8b', color: '#3f8576', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}
                                        >
                                            기본 이미지 사용
                                        </button>
                                    </div>
                                ) : (
                                    // 선택됨: 16:9 프리뷰 + 변경/삭제
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                        <div style={{ position: 'relative', width: '240px', aspectRatio: '16 / 9', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                                            <img
                                                src={field.value}
                                                alt="썸네일 미리보기"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                            {isDefaultImg && (
                                                <span style={{ position: 'absolute', top: '8px', left: '8px', padding: '2px 8px', fontSize: '11px', fontWeight: 700, color: '#fff', background: 'rgba(63,133,118,0.85)', borderRadius: '6px' }}>기본 이미지</span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <button
                                                type="button"
                                                onClick={openPicker}
                                                disabled={thumbUploading}
                                                style={{ padding: '9px 16px', fontSize: '13px', fontWeight: 600, border: '1px solid #5b9a8b', color: '#3f8576', background: '#fff', borderRadius: '8px', cursor: thumbUploading ? 'not-allowed' : 'pointer' }}
                                            >
                                                {thumbUploading ? <ButtonSpinner /> : '이미지 변경'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => field.onChange('')}
                                                style={{ padding: '9px 16px', fontSize: '13px', fontWeight: 600, border: '1px solid #e5e7eb', color: '#dc2626', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <Text size="12px" textcolor="#8B8D94" margin="2px 0 0">치과 사진이 없으면 기본 이미지를 사용하세요. (권장 16:9, 예: 1600×900)</Text>
                            </div>
                        );
                    }}
                />
            </FormGroup>

            <SubmitNote>입력하신 치과 정보가 확인되면 가입과 함께 치과 인증이 끝나 바로 채용 공고를 올릴 수 있어요.</SubmitNote>
            <ButtonGroup>
                <CancelButton type="button" onClick={onBack}>
                    <KeyboardBackspaceIcon fontSize="small" /> 이전
                </CancelButton>
                <SubmitButton
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleSubmit(onSubmit, handleInvalidSubmit)}
                >
                    {isSubmitting ? <ButtonSpinner /> : '회원가입'}
                </SubmitButton>
            </ButtonGroup>
        </FormContent>
    );
};

export default DentalSignUpForm;
